import React from 'react';
import PropTypes from 'prop-types';
import { SpriteSvg } from 'project/sprite-svg';
import { LiveEventSelect } from 'components/live-event-select';
import { LazyImage } from 'project/lazy-image';
import { FLAG_IMAGE_PATH } from 'general/js/constants';
import {
    EVENT_FAVOURITE_PLAYER_SERVICE_ADD,
    EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE
} from 'general/js/services/favorite-player-services';
import { FavoriteButton } from 'components/favorite-button';
import eventBus from 'general/js/event-bus';
import { favoritePlayerService } from 'general/js/services';
import gtm from 'general/js/gtm';
import { bemClasses, LivePreMessage } from 'components/live-pre-message';
import QualLeaderboardScoringInfo from './qual-leaderboard-scoring-info';
import QualLeaderboardTable from './qual-leaderboard-table';
import QualLeaderboardArticle from './qual-leaderboard-article';
import { cellTypes, noFavoriteMessage, noResultsMessage } from './enums';
import { qualSortingStrategies } from './qualSortingStrategies';
import {
    DEFAULT_VIEW,
    HOLES_VIEW
} from './qual-leaderboard-const';
import QualLeaderboardSearchInput from './qual-leaderboard-search-input';


class QualLeaderboardContainer extends React.Component {
    constructor(props) {
        super(props);

        const currentRound = props.location.currentRound && props.location.currentRound.toString();
        this.defaultRoundId = currentRound || '1';

        this.state = {
            view: DEFAULT_VIEW,
            round: this.defaultRoundId,
            scoringInfoClassName: false,
            sortingType: cellTypes.POSITION,
            isSortingAscending: true,
            favoritePlayerList: favoritePlayerService.getAll(),
            applyFavourite: false,
            applySearchPlayers: false,
        };
    }


    componentDidMount() {
        eventBus.addListener(EVENT_FAVOURITE_PLAYER_SERVICE_ADD, this.renderFavoritePlayers);
        eventBus.addListener(EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE, this.renderFavoritePlayers);
    }

    componentWillUnmount() {
        eventBus.removeListener(EVENT_FAVOURITE_PLAYER_SERVICE_ADD, this.renderFavoritePlayers);
        eventBus.removeListener(EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE, this.renderFavoritePlayers);
    }


    renderFavoritePlayers = () => {
        this.setState({
            favoritePlayerList: favoritePlayerService.getAll()
        });
    }

    changeView = () => {
        if (this.state.view === DEFAULT_VIEW) {
            this.setState({ view: HOLES_VIEW });
        } else {
            this.setState({ view: DEFAULT_VIEW, round: this.defaultRoundId });
        }
    }

    renderClassName = () => {
        if (this.state.view === HOLES_VIEW) {
            return 'is-holes-view';
        }

        return '';
    }

    getChoices = () => {
        const roundChoices = [
            { label: 'Round 1', value: '1', selected: false },
            { label: 'Round 2', value: '2', selected: false }
        ];

        roundChoices.forEach((choice) => {
            choice.selected = choice.value === this.state.round.toString();
        });

        return roundChoices;
    }

    handleChangeSelect = (value) => {
        this.setState({ round: value });
    }

    renderSelectControl = () => {
        if (this.state.view === HOLES_VIEW && this.props.location.rndAvailable === 2) {
            return (
                <div className="qual-leaderboard__select">
                    <LiveEventSelect
                        choices={this.getChoices()}
                        onChange={this.handleChangeSelect}
                        theme={{ isBlack: 'is-black' }}
                    />
                </div>
            );
        }

        return null;
    };

    showScoringInfoContent = () => {
        this.setState({ scoringInfoClassName: !this.state.scoringInfoClassName });
    };

    sortStateInit = (playersData) => {
        const currentStrategy = qualSortingStrategies[this.state.sortingType];
        return currentStrategy.filteringFunc(playersData, this.state.isSortingAscending);
    };

    sortingHandler = (type) => {
        let isSortingAscending = this.state.isSortingAscending;

        if (this.state.sortingType === type) {
            isSortingAscending = !isSortingAscending;
        } else {
            isSortingAscending = true;
        }

        this.setState({
            sortingType: type,
            isSortingAscending
        });
    };

    toggleFilterByFavourite = () => {
        if (!this.state.applyFavourite) {
            gtm.push('Leaderboard', 'Favourites', 'Select Favourites');
        }
        this.setState({ applyFavourite: !this.state.applyFavourite });
    }

    filterByFavorite = (playersData) => {
        return playersData.filter((player) =>
            favoritePlayerService.isFavourite(player.entryId.toString()));
    }

    filterByfilterBySearch = ({ players, searchQuery }) => {
        if (!players || !Array.isArray(players)) {
            return [];
        }

        const filterBy = ['firstName', 'lastName'];

        return players.filter((item) => {
            return filterBy.some((prop) => {
                return searchQuery.some((val) => {
                    return item[prop].toLowerCase().includes(val);
                });
            });
        });
    }

    filteredStateInit = (data) => {
        const props = {
            players: data ? data.players : [],
            searchQuery: this.searchQuery
        };

        return this.filterByfilterBySearch(props);
    }

    processData(players) {
        const { applyFavourite } = this.state;
        let processedPlayers = players;

        if (applyFavourite) {
            processedPlayers = this.filterByFavorite(processedPlayers);
        }

        if (this.state.applySearchPlayers) {
            processedPlayers = this.filteredStateInit({ players: processedPlayers, searchQuery: 'searchPlayers' });
        }

        processedPlayers = this.sortStateInit(processedPlayers);

        return processedPlayers;
    }

    getDefaultCallback = (term) => {
        this.searchQuery = term ? term.trim().toLowerCase().split(/\s+/) : '';

        this.setState({ applySearchPlayers: term !== '' });
    }

    renderTable = () => {
        const players = this.processData(this.props.location.players);

        if (this.state.applyFavourite && !players.length) {
            return <LivePreMessage model={noFavoriteMessage} bemList={[bemClasses.DARK]}/>;
        }

        if (this.state.applySearchPlayers && !players.length) {
            return <LivePreMessage model={noResultsMessage} bemList={[bemClasses.DARK]}/>;
        }

        return <QualLeaderboardTable
            {...this.props}
            view={this.state.view}
            selectedRoundId={this.state.round}
            players={players}
            favoritePlayers={this.state.favoritePlayerList}
            processingState={{
                sortingType: this.state.sortingType,
                isSortingAscending: this.state.isSortingAscending
            }}
            sortingHandler={(type) => this.sortingHandler(type)}
        />;
    };

    render() {
        return (
            <React.Fragment>
                {
                    this.props.location.competitionStatus &&
                    <div className="qual-leaderboard__article-wrapper is-mobile">
                        <QualLeaderboardArticle location={this.props.location} />
                    </div>
                }
                {
                    this.props.options.infoInlineRichText &&
                    <div className="qual-leaderboard__info-inline-wrapper is-mobile">
                        <div dangerouslySetInnerHTML={{ __html: this.props.options.infoInlineRichText }} />
                    </div>
                }
                <div className='qual-leaderboard__search-wrapper is-mobile'>
                    <QualLeaderboardSearchInput
                        defaultCallback={this.getDefaultCallback}
                    />
                </div>
                <div className="qual-leaderboard__control-panel">
                    <div className="qual-leaderboard__control-panel-col-1">
                        <div className="qual-leaderboard__article-wrapper is-desktop">
                            {
                                <QualLeaderboardArticle location={this.props.location} />
                            }
                        </div>
                        {this.renderSelectControl()}
                    </div>
                    <div className='qual-leaderboard__search-wrapper is-desktop'>
                        <QualLeaderboardSearchInput
                            defaultCallback={this.getDefaultCallback}
                        />
                    </div>
                    <div className="qual-leaderboard__control-panel-col-2 is-desktop">
                        {this.props.options.infoInlineRichText &&
                             <div className="qual-leaderboard__info-inline" dangerouslySetInnerHTML={{ __html: this.props.options.infoInlineRichText }} />}
                    </div>
                    <div className="qual-leaderboard__control-panel-col-3">
                        <QualLeaderboardScoringInfo
                            scoringInfoClassName={this.state.scoringInfoClassName}
                            clicked={this.showScoringInfoContent}
                        />
                        <FavoriteButton
                            childCss={'qual-leaderboard__favourites'}
                            stateFavoriteButton={this.state.applyFavourite}
                            onClick={this.toggleFilterByFavourite}
                        />
                        <div className="qual-leaderboard__switch-button-wrapper">
                            <button
                                className={`qual-leaderboard__switch-button ${this.renderClassName()}`}
                                type="button"
                                onClick={this.changeView}
                            >
                                <div className="qual-leaderboard__switch-button-text">hole by hole</div>
                                <div
                                    className="qual-leaderboard__switch-button-icon qual-leaderboard__switch-button-icon-flag-wrapper">
                                    <div className="qual-leaderboard__switch-button-icon-flag">
                                        <LazyImage image={FLAG_IMAGE_PATH} />
                                    </div>
                                </div>
                                <SpriteSvg
                                    childCss="qual-leaderboard__switch-button-icon qual-leaderboard__switch-button-icon-close"
                                    iconName="cross"
                                    width="10"
                                    height="10"
                                />
                            </button>
                        </div>
                    </div>
                </div>
                {this.renderTable()}
            </React.Fragment>
        );
    }
}

QualLeaderboardContainer.propTypes = {
    options: PropTypes.object,
    location: PropTypes.object,
};

export default QualLeaderboardContainer;
